/**
 * URL-prefix locale routing for VietClinics.
 *
 * Patterns supported:
 *   /                            → English (default, no prefix)
 *   /treatments                  → English
 *   /vi                          → Vietnamese homepage
 *   /vi/treatments               → Vietnamese treatments page
 *   /fr/treatments/dental-implants → French dental implants page
 *
 * Implementation:
 *  - Reads the first path segment from `useLocation()`.
 *  - If it matches one of the non-default locale codes (vi, fr, de, ar, zh, ko, ja, ru),
 *    syncs LanguageContext to that code and strips the prefix from the location
 *    that's passed to the inner <Routes>.
 *  - Otherwise renders normally; English content is served at root paths.
 *  - Locale changes via the language switcher rewrite the URL via
 *    `useLocalizedPath()` / `Link` helpers below so users stay on the same page.
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link as RRLink } from 'react-router-dom';
import { useLanguage, SUPPORTED_CODES } from '../context/LanguageContext';

const NON_DEFAULT_LOCALES = SUPPORTED_CODES.filter((c) => c !== 'en');
const LOCALE_REGEX = new RegExp(`^/(${NON_DEFAULT_LOCALES.join('|')})(?=/|$)`);

export function parsePathLocale(pathname) {
  const match = pathname.match(LOCALE_REGEX);
  if (match) {
    const locale = match[1];
    const stripped = pathname.replace(LOCALE_REGEX, '') || '/';
    return { locale, stripped };
  }
  return { locale: 'en', stripped: pathname };
}

export function buildLocalizedPath(path, locale) {
  if (!path.startsWith('/')) path = '/' + path;
  if (locale === 'en') return path;
  return `/${locale}${path === '/' ? '' : path}`;
}

/* Use this hook in any switcher to navigate to the same page in a new locale */
export const useLocalizedNavigate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  return (newLocale) => {
    const { stripped } = parsePathLocale(location.pathname);
    navigate(buildLocalizedPath(stripped, newLocale) + location.search + location.hash);
  };
};

/* Drop-in <Link> replacement that prefixes the current locale automatically */
export const L = ({ to, ...rest }) => {
  const { language } = useLanguage();
  if (typeof to !== 'string') return <RRLink to={to} {...rest} />;
  // Don't double-prefix paths that already start with /vi/, /fr/, etc.
  if (LOCALE_REGEX.test(to)) return <RRLink to={to} {...rest} />;
  return <RRLink to={buildLocalizedPath(to, language)} {...rest} />;
};

/* Top-level wrapper used inside <BrowserRouter>. It keeps language context in
 * sync with the URL prefix and re-renders children with the stripped path so
 * the normal route table matches without modification. */
export const LocaleRouter = ({ children }) => {
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();
  const { locale } = parsePathLocale(location.pathname);

  useEffect(() => {
    if (locale !== language) changeLanguage(locale);
  }, [locale, language, changeLanguage]);

  return <>{children}</>;
};

export default LocaleRouter;
